// The MIT License (MIT)
//
// Copyright (c) 2021 Camptocamp SA
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import angular from 'angular';
import pnFormService from '../formservices/formService.js';
import './pronaturaForm.scss';

/**
 * @type {angular.IModule}
 * @hidden
 */ const myModule = angular.module('pnForm', [pnFormService.name]);

myModule.run(
  /**
   * @ngInject
   * @param {angular.ITemplateCacheService} $templateCache
   */
  ($templateCache) => {
    // @ts-ignore: webpack
    $templateCache.put('pn/pronaturaForm', require('./pronaturaForm.html'));
  }
);

myModule.value(
  'pnFormTemplateUrl',
  /**
   * @param {angular.IAttributes} $attrs Attributes.
   * @return {string} Template URL.
   */
  ($attrs) => {
    const templateUrl = $attrs.pnFormTemplateUrl;
    return templateUrl !== undefined ? templateUrl : 'pn/pronaturaForm';
  }
);

/**
 * @param {angular.IAttributes} $attrs Attributes.
 * @param {function(angular.IAttributes): string} pnFormTemplateUrl Template function.
 * @return {string} Template URL.
 * @ngInject
 * @private
 * @hidden
 */
function pnFormTemplateUrl($attrs, pnFormTemplateUrl) {
  return pnFormTemplateUrl($attrs);
}

/**
 * A form for non geo referenced data
 *
 * Example:
 *
 *   <pn-form ng-if="ctrl.item !== null"
 *     pn-form-item="ctrl.item"
 *     pn-show-editing="ctrl.showItemEditing"
 *     pn-item-type="ctrl.listType"
 *     pn-new-item-ready="ctrl.newItemReady"
 *     pn-show-server-error="ctrl.showServerError"
 *     pn-server-error-message="ctrl.serverErrorMessage"
 *     pn-server-error-type="ctrl.serverErrorType">
 *   </pn-form>
 *
 * @ngdoc component
 * @ngname pnForm
 */
const formComponent = {
  controller: 'pnFormController as ctrl',
  bindings: {
    item: '=pnFormItem',
    showEditing: '=pnShowEditing',
    itemType: '=pnItemType',
    newItemReady: '=pnNewItemReady',
    showServerError: '=pnShowServerError',
    serverErrorMessage: '=pnServerErrorMessage',
    serverErrorType: '=pnServerErrorType',
  },
  templateUrl: pnFormTemplateUrl,
};

myModule.component('pnForm', formComponent);

/**
 * @param {angular.IScope} $scope Angular scope.
 * @param {angular.ITimeoutService} $timeout Angular timeout service.
 * @param {JQuery} $element Element.
 * @param {import("../formservices/formService.js").FormService} pnFormService The service for form items
 * @constructor
 * @private
 * @hidden
 * @ngInject
 * @ngdoc controller
 * @ngname pnFormController
 */
export function FormController($scope, $timeout, $element, pnFormService) {
  /**
   * @type {angular.IScope}
   */
  this.$scope_ = $scope;

  /**
   * @type {angular.ITimeoutService}
   * @private
   */
  this.timeout_ = $timeout;

  /**
   * @type {JQuery}
   * @private
   */
  this.element_ = $element;

  /**
   * If the form has changed
   * @type boolean
   */
  this.dirty = false;

  /**
   * If the form is visible
   * @type boolean
   */
  this.showEditing = null;

  /**
   * The item to edit
   * @type {FormItem}
   */
  this.item = null;

  /**
   * @type {import("./FormService.js").FormService}
   * @private
   */
  this.formService_ = pnFormService;

  /**
   * If the form is pending (used by the attributes component)
   * @type {boolean} If the form is pending
   */
  this.pending = false;

  /**
   * Definition of the attributes of the form (depending on item type)
   * @type {Object}
   */
  this.attributes = null;

  /**
   * To set to true when new item is created
   * @type {Boolean}
   */
  this.newItemReady = false;

  /**
   * @type {boolean}
   */
  this.showServerError = false;

  /**
   * @type {?string}
   */
  this.serverErrorMessage = null;

  /**
   * @type {?string}
   */
  this.serverErrorType = null;

  // TODO: get the attributes from backend
  this.attributesContract = [
    {
      name: 'vertragsnummer',
      required: true,
      type: 'text',
    },
    {
      name: 'vertrag',
      required: true,
      type: 'select',
      choices: ['Basisvertrag', 'Folgevertrag'],
    },
    {
      name: 'vertragspartner_naturschutz',
      required: true,
      type: 'text',
    },
    {
      name: 'vertragspartner',
      required: true,
      type: 'text',
    },
    {
      name: 'vertragstyp',
      required: true,
      type: 'select',
      choices: [
        'neueinteilen',
        'Servitut',
        'Pflegevertrag',
        'Gebrauchsleihe',
        'anderes',
        'Kaufvertrag',
        'Schenkung, Erbschaft',
        'Tauschvertrag, Neuzuteilung',
        'Nutzungsentgelt',
        'landw. Pacht',
      ],
    },
    {
      name: 'bestimmungen',
      required: false,
      type: 'text',
    },
    {
      name: 'zahlung_zs_sektion',
      required: false,
      type: 'select',
      choices: ['Sektion', 'ZS', 'NA'],
    },
    {
      name: 'Verantwortlich',
      required: true,
      type: 'text',
    },
    {
      name: 'wiederkehrende_zahlung',
      required: false,
      type: 'boolean',
    },
    {
      name: 'vertragsbeginn',
      required: true,
      type: 'datetime',
    },
    {
      name: 'ablaufdatum',
      required: false,
      type: 'datetime',
    },
    {
      name: 'verlaengerung',
      required: false,
      type: 'number',
    },
    {
      name: 'kuendigungsdatum',
      required: false,
      type: 'datetime',
    },
    {
      name: 'vertrag_location',
      required: false,
      type: 'text',
    },
  ];

  this.attributesPolicy = [
    {
      name: 'policennr',
      required: true,
      type: 'text',
    },
    {
      name: 'versicherungstyp',
      required: true,
      type: 'select',
      choices: [
        'unbekannt',
        'andere',
        'verschiedenes',
        'Gebäudeversicherung',
        'Hausratversicherung',
        'Gebäudeversicherung inkl. Hausratversicherung',
        'Sammelpolice',
      ],
    },
    {
      name: 'versicherung',
      required: true,
      type: 'text',
    },
    {
      name: 'praemienzahler',
      readonly: false,
      required: true,
      type: 'select',
      choices: [
        'unbekannt',
        'andere',
        'Biotope und Arten',
        'Finanzen und Administration',
        'Umweltbildung',
        'Sektion',
        'Nutzer',
      ],
    },
    {
      name: 'kostenstelle',
      required: true,
      type: 'number',
    },
    {
      name: 'verantwortlich',
      required: true,
      type: 'text',
    },
    {
      name: 'police_location',
      required: false,
      type: 'text',
    },
    {
      name: 'bemerkung',
      required: false,
      type: 'text',
    },
    {
      name: 'gebaeude_id',
      required: false,
      type: 'text',
    },
  ];

  this.attributesDamage = [
    {
      name: 'schadenstyp',
      required: true,
      type: 'select',
      choices: [
        'unbekannt',
        'andere',
        'verschiedenes',
        'Einbruch',
        'Feuer',
        'Wasser',
        'Glasschaden',
        'Elementarschaden',
      ],
    },
    {
      name: 'datum',
      required: true,
      type: 'datetime',
    },
    {
      name: 'summe',
      required: false,
      type: 'number',
    },
    {
      name: 'kostenuebernahme',
      readonly: false,
      required: false,
      type: 'number',
    },
    {
      name: 'zahlungseingang',
      required: false,
      type: 'text',
    },
    {
      name: 'beschlussnr',
      required: false,
      type: 'number',
    },
    {
      name: 'wl_an',
      required: false,
      type: 'text',
    },
    {
      name: 'wl_datum',
      required: false,
      type: 'datetime',
    },
    {
      name: 'wl_betrag',
      required: false,
      type: 'number',
    },
    {
      name: 'bemerkungen',
      required: false,
      type: 'text',
    },
    {
      name: 'verantwortlich',
      required: true,
      type: 'text',
    },
  ];
}

/**
 * Init the controller
 */
FormController.prototype.$onInit = function () {
  // Set attributes
  switch (this.itemType) {
    case 'vertraege':
      this.attributes = this.attributesContract;
      break;
    case 'policen':
      this.attributes = this.attributesPolicy;
      break;
    case 'schaeden':
      this.attributes = this.attributesDamage;
      break;

    default:
      alert('Invalid form type');
  }

  //For an empty item set all properties with value null
  if (!this.item.getId()) {
    if (this.itemType === 'vertraege') {
      this.item.getValues()['ogc_fid'] = null;
    } else {
      this.item.getValues()['fid'] = null;
    }
    this.attributes.forEach((attribute) => {
      this.item.getValues()[attribute.name] = null;
    });
    this.item.setId(null);
  }
  this.setAttributes_();

  this.undoWatch = this.watchProperties();
};

/**
 * This code comes from gmf/editFeatureComponent
 */
FormController.prototype.setAttributes_ = function () {
  for (const attribute of this.attributes) {
    if (attribute.type == 'date') {
      attribute.format = 'Y-m-d';
      attribute.mask = '9999-19-39';
    } else if (attribute.type == 'time') {
      attribute.format = 'H:i';
      attribute.mask = '29:59';
    } else if (attribute.type == 'datetime') {
      attribute.format = 'Y-m-d H:i';
      attribute.mask = '9999-19-39 29:59';
    }
  }
};

/**
 *Close without saving
 */
FormController.prototype.cancel = function () {
  this.undoWatch();
  this.item = null;
  this.dirty = null;
  this.showEditing = false;
};

/**
 * Save the present item
 */
FormController.prototype.save = function () {
  if (this.item.getId()) {
    this.formService_
      .updateItem(this.item, this.itemType)
      .then((response) => {
        this.dirty = false;
      })
      .catch((error) => {
        this.showServerError = true;
        this.serverErrorType = `error type : ${error.data.error_type}`;
        this.serverErrorMessage = `error message : ${error.data.message}`;
      })
      .finally(() => {
        this.pending = false;
      });
  } else {
    this.formService_
      .createItem(this.item, this.itemType)
      .then((success) => {
        if (success.data.fid) {
          this.item.getValues()['fid'] = success.data.fid;
          this.item.setId(success.data.fid);
        } else if (success.data.ogc_fid) {
          this.item.getValues()['ogc_fid'] = success.data.ogc_fid;
          this.item.setId(success.data.ogc_fid);
        }
        this.newItemReady = true;
        this.dirty = false;
      })
      .catch((error) => {
        this.showServerError = true;
        this.serverErrorType = `error type : ${error.data.error_type}`;
        this.serverErrorMessage = `error message : ${error.data.message}`;
      })
      .finally(() => {
        this.pending = false;
      });
  }
};

/**
 * Delete the present item
 */
FormController.prototype.delete = function () {
  this.formService_
    .deleteItem(this.item.getId(), this.itemType)
    .then((success) => {
      this.item.setId(null);
      this.undoWatch();
      this.item = null;
    })
    .catch((error) => {
      this.showServerError = true;
      this.serverErrorType = `error type : ${error.data.error_type}`;
      this.serverErrorMessage = `error message : ${error.data.message}`;
    })
    .finally(() => {
      this.pending = false;
    });
  this.showEditing = false;
};

/**
 * Get the id of the active item
 * @return {integer} the id
 */
FormController.prototype.getItemId = function () {
  return this.item ? this.item.getId() : null;
};

/**
 * Set watch on all properties of the item to set dirty when changed
 * @return the undoWatch method
 */
FormController.prototype.watchProperties = function () {
  return this.$scope_.$watchCollection(
    () => {
      return this.item.getValues();
    },
    (newValue, oldValue) => {
      if (newValue !== oldValue && oldValue.fid === newValue.fid && oldValue.ogc_fid === newValue.ogc_fid) {
        this.dirty = true;
      } else {
        this.dirty = false;
      }
    }
  );
};

myModule.controller('pnFormController', FormController);

export default myModule;
